import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { LogOut, Loader2, ClipboardList, Wallet, Plus, Trash2, CheckCircle2, Circle, BadgePercent, Landmark, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'alp_crew_token';
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

export default function CrewPortal() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [rates, setRates] = useState([]);
  const [taskKey, setTaskKey] = useState('labelling');
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');
  const [bank, setBank] = useState({ accountName: '', bsb: '', accountNumber: '' });
  const [bankOpen, setBankOpen] = useState(false);
  const [bankBusy, setBankBusy] = useState(false);
  const [payoutBusy, setPayoutBusy] = useState(false);

  const saveBank = async () => {
    setBankBusy(true);
    try {
      const { data: res } = await axios.put(`${API}/crew/bank`, bank, cfg());
      setBank(res.bank); setBankOpen(false);
      toast.success('Bank details saved — payouts will go to this account');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save bank details'); }
    finally { setBankBusy(false); }
  };

  const requestPayout = async () => {
    setPayoutBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/crew/request-payout`, {}, cfg());
      toast.success(`Payout of ${money(res.amount)} requested — it'll be transferred to your account`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not request payout'); }
    finally { setPayoutBusy(false); }
  };

  const load = async () => {
    try {
      const [s, r] = await Promise.all([axios.get(`${API}/crew/summary`, cfg()), axios.get(`${API}/crew/rates`, cfg())]);
      setData(s.data); setRates(r.data);
      if (s.data.bank) setBank(s.data.bank);
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY); setToken('');
    }
  };
  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token]);

  const login = async (e) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      const { data: d } = await axios.post(`${API}/crew/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, d.token); setToken(d.token);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Login failed. Check your details.');
    } finally { setBusy(false); }
  };

  const addLog = async () => {
    const q = parseFloat(qty);
    if (!q || q <= 0) { toast.error('Enter how many you did'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/crew/work-log`, { taskKey, qty: q, note }, cfg());
      setQty(''); setNote('');
      toast.success('Logged — nice work!');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not log that'); }
    finally { setBusy(false); }
  };

  const delLog = async (id) => {
    try { await axios.delete(`${API}/crew/work-log/${id}`, cfg()); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove'); }
  };

  const toggleTask = async (id) => {
    try { await axios.put(`${API}/crew/tasks/${id}/done`, {}, cfg()); load(); }
    catch (e) { toast.error('Could not update task'); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-alp-black flex items-center justify-center px-5" data-testid="crew-login-page">
        <form onSubmit={login} className="w-full max-w-sm border border-alp-line rounded-xl bg-alp-card/40 p-8">
          <p className="font-serif text-2xl text-alp-gold text-center">ALP Crew</p>
          <p className="text-alp-gray text-xs text-center mt-1 mb-6 tracking-[0.2em] uppercase">Team portal</p>
          <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Mobile number" autoComplete="tel" data-testid="crew-username"
            className="w-full bg-alp-dark border border-alp-line rounded px-4 py-3 text-sm text-alp-cream mb-3 focus:border-alp-gold outline-none" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="crew-password"
            className="w-full bg-alp-dark border border-alp-line rounded px-4 py-3 text-sm text-alp-cream mb-4 focus:border-alp-gold outline-none" />
          {err && <p className="text-red-300 text-xs mb-3" data-testid="crew-login-error">{err}</p>}
          <button disabled={busy} data-testid="crew-login-btn"
            className="w-full bg-alp-gold text-alp-black py-3 rounded text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight disabled:opacity-50">
            {busy ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen bg-alp-black flex items-center justify-center"><Loader2 className="animate-spin text-alp-gold" size={24} /></div>;

  const openTasks = data.tasks.filter((t) => t.status === 'open');
  const doneTasks = data.tasks.filter((t) => t.status === 'done');

  return (
    <div className="min-h-screen bg-alp-black pb-16" data-testid="crew-dashboard">
      <Toaster position="top-center" richColors />
      <div className="border-b border-alp-line bg-alp-card/30">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
          <div>
            <p className="font-serif text-xl text-alp-cream">G'day, {data.name} 👋</p>
            <p className="text-alp-gray text-[11px] tracking-[0.2em] uppercase">ALP Crew Portal</p>
          </div>
          <button onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(''); }} data-testid="crew-logout"
            className="inline-flex items-center gap-1.5 text-alp-gray hover:text-alp-gold text-xs tracking-[0.15em] uppercase"><LogOut size={14} /> Out</button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 mt-6 space-y-8">
        {/* Earnings */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="crew-earnings">
          {[['To be paid', money(data.totalOwed), 'text-alp-gold'],
            ['Work owing', money(data.work.workOwed), 'text-alp-cream'],
            ['Commission owing', money(data.commission.commissionOwed), 'text-emerald-300'],
            ['Paid to date', money(data.work.workPaid + data.commission.commissionPaid), 'text-alp-gray']].map(([l, v, c]) => (
            <div key={l} className="border border-alp-line rounded-lg bg-alp-card/40 p-4">
              <p className="text-[9px] tracking-[0.2em] uppercase text-alp-gray">{l}</p>
              <p className={`text-xl mt-1 ${c}`}>{v}</p>
            </div>
          ))}
        </div>

        {/* Get paid */}
        <div className="border border-alp-gold/30 rounded-lg bg-alp-card/40 p-5" data-testid="crew-get-paid">
          <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3 flex items-center gap-1.5"><Banknote size={13} /> Get paid</p>
          {data.payoutRequest ? (
            <div className="border border-amber-400/40 rounded px-4 py-3 mb-3" data-testid="crew-payout-pending">
              <p className="text-amber-300 text-sm">Payout of {money(data.payoutRequest.amount)} requested on {new Date(data.payoutRequest.requestedAt).toLocaleDateString('en-AU')} — it'll be transferred to your account soon.</p>
            </div>
          ) : (
            <button onClick={requestPayout} disabled={payoutBusy || data.totalOwed <= 0} data-testid="crew-request-payout"
              className="w-full mb-3 inline-flex items-center justify-center gap-2 bg-alp-gold text-alp-black px-5 py-3.5 rounded text-xs tracking-[0.15em] uppercase font-semibold hover:bg-alp-goldlight disabled:opacity-40">
              {payoutBusy ? <Loader2 size={14} className="animate-spin" /> : <Banknote size={15} />}
              {data.totalOwed > 0 ? `Request payout of ${money(data.totalOwed)}` : 'Nothing owing yet'}
            </button>
          )}
          {!bankOpen && bank.accountNumber ? (
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <p className="text-alp-gray flex items-center gap-1.5" data-testid="crew-bank-summary">
                <Landmark size={12} className="text-alp-gold" /> Paying to: <span className="text-alp-cream/90">{bank.accountName}</span> · BSB {bank.bsb} · Acc {bank.accountNumber}
              </p>
              <button onClick={() => setBankOpen(true)} data-testid="crew-bank-edit" className="text-alp-gold underline underline-offset-4 hover:text-alp-goldlight">Change</button>
            </div>
          ) : (
            <div className="space-y-2" data-testid="crew-bank-form">
              {!bank.accountNumber && !bankOpen && <p className="text-alp-gray text-xs mb-1">Add your bank details so we know where to send your money:</p>}
              <input value={bank.accountName} onChange={(e) => setBank({ ...bank, accountName: e.target.value })} placeholder="Account name" data-testid="crew-bank-name"
                className="w-full bg-alp-dark border border-alp-line rounded px-3 py-2.5 text-sm text-alp-cream focus:border-alp-gold outline-none" />
              <div className="flex gap-2">
                <input value={bank.bsb} onChange={(e) => setBank({ ...bank, bsb: e.target.value })} placeholder="BSB (6 digits)" inputMode="numeric" data-testid="crew-bank-bsb"
                  className="w-32 bg-alp-dark border border-alp-line rounded px-3 py-2.5 text-sm text-alp-cream focus:border-alp-gold outline-none" />
                <input value={bank.accountNumber} onChange={(e) => setBank({ ...bank, accountNumber: e.target.value })} placeholder="Account number" inputMode="numeric" data-testid="crew-bank-acct"
                  className="flex-1 bg-alp-dark border border-alp-line rounded px-3 py-2.5 text-sm text-alp-cream focus:border-alp-gold outline-none" />
                <button onClick={saveBank} disabled={bankBusy} data-testid="crew-bank-save"
                  className="px-4 bg-alp-gold text-alp-black rounded text-xs tracking-[0.12em] uppercase font-semibold hover:bg-alp-goldlight disabled:opacity-50">
                  {bankBusy ? '...' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Referrals — privacy safe */}
        {data.commission.code && (
          <div className="border border-alp-gold/30 rounded-lg bg-alp-card/40 p-5" data-testid="crew-referrals">
            <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-2 flex items-center gap-1.5"><BadgePercent size={13} /> Your code: {data.commission.code}</p>
            <p className="text-alp-cream text-sm"><span className="text-2xl text-alp-gold font-serif">{data.commission.referredSales}</span> sale{data.commission.referredSales === 1 ? '' : 's'} referred · you earn {data.commission.percent}% — {money(data.commission.commissionEarned)} earned so far</p>
          </div>
        )}

        {/* My tasks */}
        <div data-testid="crew-tasks">
          <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3 flex items-center gap-1.5"><ClipboardList size={13} /> My tasks ({openTasks.length} open)</p>
          {data.tasks.length === 0 ? <p className="text-alp-gray text-sm">No tasks allocated right now.</p> : (
            <div className="space-y-2">
              {[...openTasks, ...doneTasks].map((t) => (
                <button key={t.id} onClick={() => toggleTask(t.id)} data-testid={`crew-task-${t.id}`}
                  className={`w-full flex items-start gap-3 border rounded-lg p-4 text-left transition-colors ${t.status === 'done' ? 'border-alp-line/50 opacity-50' : 'border-alp-line hover:border-alp-gold/50'}`}>
                  {t.status === 'done' ? <CheckCircle2 size={18} className="text-emerald-300 shrink-0 mt-0.5" /> : <Circle size={18} className="text-alp-gray shrink-0 mt-0.5" />}
                  <span>
                    <span className={`block text-sm ${t.status === 'done' ? 'text-alp-gray line-through' : 'text-alp-cream'}`}>{t.title}</span>
                    {t.details && <span className="block text-alp-gray text-xs mt-0.5">{t.details}</span>}
                    {t.dueDate && <span className="block text-amber-300/80 text-[11px] mt-0.5">Due {t.dueDate}</span>}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Log work */}
        <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5" data-testid="crew-log-work">
          <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3 flex items-center gap-1.5"><Wallet size={13} /> Log today's work</p>
          <div className="space-y-3">
            <select value={taskKey} onChange={(e) => setTaskKey(e.target.value)} data-testid="crew-log-type"
              className="w-full bg-alp-dark border border-alp-line rounded px-3 py-3 text-sm text-alp-cream">
              {rates.map((r) => <option key={r.key} value={r.key}>{`${r.label} — $${Number(r.rate).toFixed(2)} ${r.unit}`}</option>)}
            </select>
            <div className="flex gap-2">
              <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="crew-log-qty"
                placeholder={taskKey === 'labelling' ? 'How many lots of 10?' : taskKey === 'delivery' ? 'How many loads?' : 'How many kits?'}
                className="flex-1 bg-alp-dark border border-alp-line rounded px-3 py-3 text-sm text-alp-cream focus:border-alp-gold outline-none" />
              <button onClick={addLog} disabled={busy} data-testid="crew-log-add"
                className="px-5 bg-alp-gold text-alp-black rounded text-xs tracking-[0.15em] uppercase font-semibold hover:bg-alp-goldlight disabled:opacity-50 inline-flex items-center gap-1"><Plus size={14} /> Log</button>
            </div>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" data-testid="crew-log-note"
              className="w-full bg-alp-dark border border-alp-line rounded px-3 py-2.5 text-sm text-alp-cream focus:border-alp-gold outline-none" />
            {qty > 0 && <p className="text-emerald-300 text-xs">= {money((rates.find((r) => r.key === taskKey)?.rate || 0) * qty)}</p>}
          </div>
        </div>

        {/* History */}
        <div data-testid="crew-history">
          <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">My log</p>
          {data.logs.length === 0 ? <p className="text-alp-gray text-sm">Nothing logged yet — add your first entry above.</p> : (
            <div className="border border-alp-line rounded-lg divide-y divide-alp-line/50">
              {data.logs.map((l) => (
                <div key={l.id} className="flex items-center gap-3 px-4 py-3" data-testid={`crew-log-${l.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-alp-cream text-sm truncate">{l.label} × {l.qty}{l.note ? ` — ${l.note}` : ''}</p>
                    <p className="text-alp-gray text-[11px]">{l.date}</p>
                  </div>
                  <span className={`text-sm ${l.status === 'paid' ? 'text-alp-gray' : 'text-alp-gold'}`}>{money(l.amount)}</span>
                  <span className={`text-[9px] tracking-[0.1em] uppercase border rounded-full px-2 py-0.5 ${l.status === 'paid' ? 'text-emerald-300 border-emerald-400/40' : 'text-amber-300 border-amber-400/40'}`}>{l.status}</span>
                  {l.status !== 'paid' && (
                    <button onClick={() => delLog(l.id)} data-testid={`crew-log-del-${l.id}`} className="text-alp-gray hover:text-red-300"><Trash2 size={14} /></button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
